// Command palette (⌘K / Ctrl+K) — jump anywhere.
//   - Pages: indexed from the sidebar links (label + group + href)
//   - Departments: from #department-selector (navigates the current page
//     to ?department=<id>)
//   - Employees: search#quick (planner/admin), debounced
//   - Theme actions
// Lives on the topbar: <header data-controller="palette"> holds the trigger
// button (data-action="palette#open") and the dialog (targets). Keyboard:
// ↑/↓ move, ↵ open, Esc close; the global shortcut is a document action.
import { Controller } from '@hotwired/stimulus'
import { navigateGuarded } from '../lib/unsaved'
import { escapeHtml } from '../lib/dom'

// Group headings and the empty state come from the server (data-palette-labels-value),
// so the palette follows the interface language. The English strings below are
// only the fallback for a render without that value.
const KINDS_EN = { page: 'Pages', department: 'Departments', employee: 'Employees', action: 'Actions' }

export default class extends Controller {
  static targets = ['dialog', 'input', 'list']
  static values = { searchUrl: String, labels: Object }

  connect () {
    this.items = []
    this.remote = []
    this.active = 0
    this.seq = 0
  }

  disconnect () {
    this.close()
    clearTimeout(this.timer)
  }

  // ---- open / close --------------------------------------------
  shortcut (event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault()
      this.isOpen ? this.close() : this.open()
    }
  }

  open (event) {
    if (event) event.preventDefault()
    if (!this.hasDialogTarget) return
    this.dialogTarget.hidden = false
    document.documentElement.classList.add('palette-open')
    this.active = 0
    this.remote = []
    this.inputTarget.value = ''
    this.inputTarget.focus()
    this.compute()
  }

  close () {
    if (!this.hasDialogTarget || this.dialogTarget.hidden) return
    this.dialogTarget.hidden = true
    document.documentElement.classList.remove('palette-open')
  }

  get isOpen () { return this.hasDialogTarget && !this.dialogTarget.hidden }

  // ---- keyboard inside the dialog ------------------------------
  keydown (event) {
    if (!this.isOpen) return
    if (event.key === 'Escape') { event.preventDefault(); this.close() } else if (event.key === 'ArrowDown') {
      event.preventDefault(); if (this.items.length) { this.active = (this.active + 1) % this.items.length; this.render() }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault(); if (this.items.length) { this.active = (this.active - 1 + this.items.length) % this.items.length; this.render() }
    } else if (event.key === 'Enter') { event.preventDefault(); this.activate(this.items[this.active]) }
  }

  input () {
    this.active = 0
    this.compute()
    this.fetchEmployees(this.inputTarget.value)
  }

  pick (event) {
    const li = event.target.closest('.ui-palette__item')
    if (li) this.activate(this.items[parseInt(li.dataset.index, 10)])
  }

  // ---- index ----------------------------------------------------
  pageIndex () {
    const out = []
    document.querySelectorAll('.app-sidebar .app-nav__link').forEach((a) => {
      const label = (a.querySelector('.app-nav__label') || a).textContent.trim()
      const group = a.closest('.app-nav__group')
      const groupLabel = group ? (group.querySelector('.app-nav__group-text') || {}).textContent : ''
      if (!label || !a.getAttribute('href')) return
      out.push({ kind: 'page', label, hint: (groupLabel || '').trim(), href: a.getAttribute('href'), icon: 'fa-file' })
    })
    return out
  }

  departmentIndex () {
    const sel = document.getElementById('department-selector')
    if (!sel) return []
    const base = window.location.pathname
    return Array.from(sel.options).map((o) => ({
      kind: 'department', label: o.textContent.trim(), hint: this.label('department_hint') || 'Department', icon: 'fa-building',
      href: base + '?department=' + encodeURIComponent(o.value), current: o.selected
    }))
  }

  actionIndex () {
    return [
      { kind: 'action', label: 'Thema: Light', hint: 'Weergave', icon: 'fa-sun', theme: 'light' },
      { kind: 'action', label: 'Thema: Hybrid', hint: 'Weergave', icon: 'fa-adjust', theme: 'hybrid' },
      { kind: 'action', label: 'Thema: Dark', hint: 'Weergave', icon: 'fa-moon', theme: 'dark' },
      { kind: 'action', label: 'Thema: Auto (volg systeem)', hint: 'Weergave', icon: 'fa-desktop', theme: 'system' }
    ]
  }

  // Score: exact prefix > word prefix > substring > subsequence.
  score (q, text) {
    const t = text.toLowerCase(); const s = q.toLowerCase()
    if (!s) return 1
    if (t.startsWith(s)) return 100
    if (t.split(/\s+/).some((w) => w.startsWith(s))) return 80
    const idx = t.indexOf(s)
    if (idx >= 0) return 60 - Math.min(idx, 20)
    let i = 0
    for (const ch of t) { if (ch === s[i]) i++; if (i === s.length) return 20 }
    return 0
  }

  compute () {
    const q = this.inputTarget.value || ''
    const local = [...this.pageIndex(), ...this.departmentIndex(), ...this.actionIndex()]
      .map((it) => ({ it, s: this.score(q, it.label) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.it)
    const pages = local.filter((i) => i.kind === 'page').slice(0, q ? 8 : 12)
    const depts = local.filter((i) => i.kind === 'department').slice(0, 6)
    const actions = q ? local.filter((i) => i.kind === 'action').slice(0, 4) : []
    this.items = [...pages, ...depts, ...this.remote, ...actions]
    if (this.active >= this.items.length) this.active = 0
    this.render()
  }

  fetchEmployees (q) {
    if (!this.hasSearchUrlValue) return
    clearTimeout(this.timer)
    if (q.trim().length < 2) { this.remote = []; this.compute(); return }
    this.timer = setTimeout(() => {
      const mine = ++this.seq
      fetch(this.searchUrlValue + '?q=' + encodeURIComponent(q.trim()), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((res) => res.ok ? res.json() : { employees: [] })
        .then((data) => {
          if (mine !== this.seq) return
          this.remote = (data.employees || []).map((e) => ({ kind: 'employee', label: e.name, hint: e.department || this.label('employee_hint') || 'Employee', icon: 'fa-user', href: e.url }))
          this.compute()
        })
        .catch(() => {})
    }, 180)
  }

  // One label, from the server values with an English fallback.
  label (key) {
    const l = this.hasLabelsValue ? this.labelsValue : null
    return (l && l[key]) || KINDS_EN[key] || null
  }

  render () {
    const ul = this.listTarget
    if (!this.items.length) { ul.innerHTML = `<li class="ui-palette__empty">${escapeHtml(this.label('empty') || 'Nothing found.')}</li>`; return }
    let lastKind = null
    ul.innerHTML = this.items.map((it, i) => {
      const head = it.kind !== lastKind ? `<li class="ui-palette__group" role="presentation">${escapeHtml(this.label(it.kind) || it.kind)}</li>` : ''
      lastKind = it.kind
      return head + `<li class="ui-palette__item${i === this.active ? ' is-active' : ''}${it.current ? ' is-current' : ''}" role="option" aria-selected="${i === this.active}" data-index="${i}">
        <i class="fas ${escapeHtml(it.icon)}" aria-hidden="true"></i>
        <span class="ui-palette__label">${escapeHtml(it.label)}</span>
        <span class="ui-palette__hint">${escapeHtml(it.hint || '')}</span>
      </li>`
    }).join('')
    const el = ul.querySelector('.ui-palette__item.is-active')
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }

  activate (it) {
    if (!it) return
    if (it.kind === 'action' && it.theme) {
      const btn = document.querySelector(`[data-set-theme="${it.theme}"]`)
      if (btn) btn.click()
      this.close(); return
    }
    // Via de gedeelde guard: springen vanuit het palet mag geen bewerking
    // stilzwijgend weggooien (en geen kaal browservenster tonen).
    if (it.href) navigateGuarded(it.href)
  }
}
